import { ColourSwatch, ProductOptionKeys } from '../../../types/enterpriseTypes'
import { InStockLocation } from '../../generated/graphql'

interface Product {
  sku: string
  options: {
    key: string
    choices: {
      colour?: string
      key: string
      title: string
    }[]
  }[]
  content?: {
    key: string
    value: {
      stringValue: string
      __typename: 'ProductContentStringValue'
    }
  }[]
  variants: {
    title: string
    inStockLocations?: string[]
    choices: {
      key: string
      optionKey: string
    }[]
    content?: {
      key: string
      value: {
        stringValue: string
        __typename: 'ProductContentStringValue'
      }
    }[]
    inStock: boolean
    product?: {
      sku: string
      url?: string
    } | null
  }[]
}

export const findOption = (product: Product, optionKey: string) => {
  return product.options.find((option) => option.key === optionKey)
}

export const getColourSwatches = <ProductType extends Product>(
  product: ProductType | undefined,
  configs: {
    alternateProductKeysForSwatches?: string[] | undefined
    contentValueToApplyAlternateSwatch?: string | undefined
    contentKeyToApplyAlternateSwatch?: string | undefined
    colourSwatchContentKey?: string | undefined
  },
): ColourSwatch[] => {
  if (!product || !product.options) {
    return []
  }

  const colourOption = findOption(product, ProductOptionKeys.COLOUR)
  if (!colourOption && configs.alternateProductKeysForSwatches) {
    const { colourHexCodes } = product.variants.reduce(
      (accumulator, variant) => {
        const stockedProductCheck =
          !variant.inStockLocations?.includes(InStockLocation.Warehouse) ||
          !variant?.inStockLocations?.includes(InStockLocation.Store)

        if (
          variant.choices.length === 0 ||
          (!variant.inStock && stockedProductCheck)
        ) {
          return accumulator
        }
        const {
          colourHexCode,
          displaySwatch,
          colourKey,
          colourTitle,
        } = variant.content?.reduce<{
          colourHexCode: string
          displaySwatch: boolean
          colourKey: string
          colourTitle: string
        }>(
          (contentAccumulator, content) => {
            if (content.key === configs.colourSwatchContentKey) {
              contentAccumulator.colourHexCode = `#${content.value.stringValue.toLowerCase()}`
              contentAccumulator.colourKey = contentAccumulator.colourHexCode
              contentAccumulator.colourTitle = content.value.stringValue
              return contentAccumulator
            }
            if (
              content.key === configs.contentKeyToApplyAlternateSwatch &&
              content.value.stringValue ===
                configs.contentValueToApplyAlternateSwatch
            ) {
              contentAccumulator.displaySwatch = true
              return contentAccumulator
            }
            return contentAccumulator
          },
          {
            colourHexCode: '',
            colourKey: '',
            colourTitle: '',
            displaySwatch: false,
          },
        )
        const href = variant?.product?.url
        if (href && colourHexCode && displaySwatch) {
          if (!accumulator.variantHexCodes.includes(colourHexCode)) {
            accumulator.variantHexCodes.push(colourHexCode)
            accumulator.colourHexCodes.add({
              href,
              key: colourKey,
              title: colourTitle,
              colour: colourHexCode,
              __typename: 'ColourSwatch',
            })
          }
        }
        return accumulator
      },
      {
        variantHexCodes: new Array<String>(),
        colourHexCodes: new Set<ColourSwatch>(),
      },
    )
    return Array.from(colourHexCodes)
  }
  return (
    product.options
      .find((option) => option.key === ProductOptionKeys.COLOUR)
      ?.choices.reduce(
        (swatches: NonNullable<ColourSwatch[]>, colourChoice) => {
          const currentVariant = product.variants.find(
            (variant) =>
              variant.inStock &&
              variant.choices.find((choice) => choice.key === colourChoice.key),
          )
          const href = currentVariant?.product?.url
          if (href && colourChoice.colour) {
            const swatch = {
              ...colourChoice,
              href,
              colour: colourChoice.colour,
              __typename: 'ColourSwatch',
            }

            if (colourChoice.colour === 'Multi') {
              swatches.unshift(swatch)
            } else {
              swatches.push(swatch)
            }
          }
          return swatches
        },
        [],
      ) || []
  )
}
